import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";

interface UseActivityTrackerOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
}

export function useActivityTracker({
  timeoutMinutes = 60, // 1 hour
  warningMinutes = 5, // 5 minutes before timeout
  onWarning,
  onTimeout,
}: UseActivityTrackerOptions = {}) {
  const { user, signOut } = useAuth();
  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;

    // Clear existing timers
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (warningTimeoutIdRef.current) {
      clearTimeout(warningTimeoutIdRef.current);
    }

    // Only set timers if user is logged in
    if (!user) return;

    // Set warning timer
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000;
    warningTimeoutIdRef.current = setTimeout(() => {
      if (!warningShownRef.current) {
        warningShownRef.current = true;
        onWarning?.();

        // Dispatch a global event for session warning
        window.dispatchEvent(
          new CustomEvent("sessionWarning", {
            detail: {
              message: `Your session will expire in ${warningMinutes} minutes due to inactivity. Please interact with the page to stay logged in.`,
            },
          })
        );
      }
    }, warningTime);

    // Set timeout timer
    const timeoutTime = timeoutMinutes * 60 * 1000;
    timeoutIdRef.current = setTimeout(async () => {
      console.log("🔐 Session timeout due to inactivity, signing out...");

      // Dispatch timeout event
      onTimeout?.();

      // Show final message
      window.dispatchEvent(
        new CustomEvent("sessionWarning", {
          detail: {
            message:
              "Your session has expired due to inactivity. You will be signed out.",
          },
        })
      );

      // Sign out after a brief delay to show the message
      setTimeout(() => {
        signOut();
      }, 2000);
    }, timeoutTime);
  }, [user, timeoutMinutes, warningMinutes, onWarning, onTimeout, signOut]);

  const handleActivity = useCallback(() => {
    // Only track activity if user is logged in
    if (!user) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Only reset timer if more than 30 seconds have passed since last activity
    // This prevents excessive timer resets from rapid events
    if (timeSinceLastActivity > 30000) {
      resetTimer();
    }
  }, [user, resetTimer]);

  useEffect(() => {
    // Start tracking when user logs in
    if (user) {
      resetTimer();

      // List of events to track for user activity
      const events = [
        "mousedown",
        "mousemove",
        "keypress",
        "scroll",
        "touchstart",
        "click",
      ];

      // Add event listeners
      events.forEach((event) => {
        document.addEventListener(event, handleActivity, true);
      });

      // Cleanup function
      return () => {
        events.forEach((event) => {
          document.removeEventListener(event, handleActivity, true);
        });

        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
        if (warningTimeoutIdRef.current) {
          clearTimeout(warningTimeoutIdRef.current);
        }
      };
    } else {
      // User logged out, clear timers
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (warningTimeoutIdRef.current) {
        clearTimeout(warningTimeoutIdRef.current);
      }
    }
  }, [user, handleActivity, resetTimer]);

  // Provide methods to manually extend session
  const extendSession = useCallback(() => {
    if (user) {
      resetTimer();

      // Clear any existing warning
      window.dispatchEvent(new CustomEvent("clearSessionWarning"));
    }
  }, [user, resetTimer]);

  const getRemainingTime = useCallback(() => {
    if (!user) return 0;

    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = timeoutMinutes * 60 * 1000 - elapsed;
    return Math.max(0, remaining);
  }, [user, timeoutMinutes]);

  return {
    extendSession,
    getRemainingTime,
    resetTimer,
  };
}
